import { useState, useCallback } from "react";

const EMPTY = {
  name: "",
  invoiceDeadlineType: "",
  invoiceDeadline: "",
  invoiceDeadlineMonth: "CURRENT",
  paymentRuleType: "",
  paymentRuleValue: "",
  paymentRuleMonth: "NEXT",
  closingDay: "",
};

/**
 * Lista do validateAll → mapa id do campo → mensagem com inicial maiúscula e ponto
 * (o `$errors` de cada campo). Vale o primeiro erro de cada campo.
 * @param {{ field: string, message: string }[]} errors
 */
export function toFieldErrors(errors) {
  const map = {};
  errors.forEach((e) => {
    if (!map[e.field]) map[e.field] = `${e.message.charAt(0).toUpperCase()}${e.message.slice(1)}.`;
  });
  return map;
}

/**
 * Estado, validação e payload do formulário de padrão de contrato.
 *
 * Existe porque as três telas que editam padrão (cadastro, edição e o modal
 * aberto de dentro do formulário de contrato) tinham a mesma lógica copiada.
 * Cada campo novo precisava ser adicionado em três lugares — e o `closing_day`
 * seria o quarto a passar por isso.
 *
 * Os inputs são controlados por string (o projeto não usa lib de formulário),
 * então a hidratação converte tudo para string e o payload converte de volta.
 */
export default function useContractPatternForm() {
  const [values, setValues] = useState(EMPTY);

  const setField = useCallback((field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
  }, []);

  const hydrate = useCallback((pattern) => {
    if (!pattern) return;
    setValues({
      name: pattern.name || "",
      invoiceDeadlineType: pattern.invoice_deadline_type || "",
      invoiceDeadline: pattern.invoice_deadline ? String(pattern.invoice_deadline) : "",
      invoiceDeadlineMonth: pattern.invoice_deadline_month || "CURRENT",
      paymentRuleType: pattern.payment_rule_type || "",
      paymentRuleValue: pattern.payment_rule_value ? String(pattern.payment_rule_value) : "",
      paymentRuleMonth: pattern.payment_rule_month || "NEXT",
      closingDay: pattern.closing_day ? String(pattern.closing_day) : "",
    });
  }, []);

  const reset = useCallback(() => setValues(EMPTY), []);

  /**
   * Todos os erros, na ordem dos campos na tela. `field` é o id do campo em
   * ContractPatternFields, onde a mensagem aparece.
   *
   * Além das regras de sempre, confere a faixa dos dias, que antes ficava com o
   * navegador (`min`/`max` dos inputs dentro do <form>). Os
   * formulários não usam a validação do navegador e no modal o botão nem é
   * submit, então a faixa tem que ser em JS, senão um dia 99 chega no backend.
   *
   * @returns {{ field: string, label: string, message: string }[]}
   */
  const validateAll = useCallback(() => {
    const errors = [];
    const add = (field, label, message) => errors.push({ field, label, message });
    const outOfRange = (value, type) => {
      const day = Number(value);
      const max = type === "FIXED_DAY" ? 31 : 10;
      return !Number.isInteger(day) || day < 1 || day > max ? max : null;
    };

    if (!values.name.trim()) add("pattern-name", "Nome do padrão", "informe o nome do padrão");

    if (!values.invoiceDeadlineType) {
      add("pattern-invoice-type", "Tipo de prazo", "selecione o tipo do prazo para envio da nota");
    } else if (!values.invoiceDeadline) {
      add("pattern-invoice-day", "Prazo da nota fiscal", "informe o dia do prazo para envio da nota");
    } else {
      const max = outOfRange(values.invoiceDeadline, values.invoiceDeadlineType);
      if (max) add("pattern-invoice-day", "Prazo da nota fiscal", `informe um dia entre 1 e ${max}`);
    }

    if (!values.paymentRuleType) {
      add("pattern-payment-type", "Tipo de regra", "selecione o tipo da regra de pagamento");
    } else if (values.paymentRuleType !== "LAST_DAY") {
      if (!values.paymentRuleValue) {
        add("pattern-payment-day", "Regra de pagamento", "informe o dia do pagamento");
      } else {
        const max = outOfRange(values.paymentRuleValue, values.paymentRuleType);
        if (max) add("pattern-payment-day", "Regra de pagamento", `informe um dia entre 1 e ${max}`);
      }
    }

    if (values.closingDay !== "") {
      const day = parseInt(values.closingDay, 10);
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        add("pattern-closing-day", "Dia de fechamento", "informe um dia entre 1 e 31");
      }
    }
    return errors;
  }, [values]);

  const toPayload = useCallback(
    () => ({
      name: values.name.trim(),
      invoice_deadline: parseInt(values.invoiceDeadline),
      invoice_deadline_type: values.invoiceDeadlineType,
      invoice_deadline_month: values.invoiceDeadlineMonth,
      payment_rule_type: values.paymentRuleType,
      payment_rule_value:
        values.paymentRuleType !== "LAST_DAY" ? parseInt(values.paymentRuleValue) : null,
      payment_rule_month: values.paymentRuleMonth,
      closing_day: values.closingDay === "" ? null : parseInt(values.closingDay, 10),
    }),
    [values]
  );

  return { values, setField, hydrate, reset, validateAll, toPayload };
}
